// src/hooks/useCamera.js - Version simplifiée qui fonctionnait
import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Custom hook for camera functionality - SIMPLIFIED VERSION
 * @param {Function} onBarcodeDetected - Callback when barcode is detected
 * @returns {Object} Camera state and methods
 */
export const useCamera = (onBarcodeDetected) => {
  const [isOpen, setIsOpen] = useState(false);
  const [error, setError] = useState(null);
  const [stream, setStream] = useState(null);
  
  const videoRef = useRef(null);

  /**
   * Start camera stream - SIMPLE VERSION THAT WORKED
   */
  const startCamera = useCallback(async () => {
    setError(null);
    
    try {
      if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
        console.log('🎥 Requesting camera access...');
        
        const mediaStream = await navigator.mediaDevices.getUserMedia({ 
          video: { facingMode: 'environment' }
        });
        
        setStream(mediaStream);
        setIsOpen(true);
        console.log('✅ Camera started successfully');

        // Attach stream to video element
        if (videoRef.current) {
          videoRef.current.srcObject = mediaStream;
          videoRef.current.play();
        }
      } else {
        throw new Error('Caméra non supportée sur cet appareil');
      }
    } catch (error) {
      console.error('❌ Camera error:', error);
      setError(getSimpleErrorMessage(error));
      setIsOpen(false);
    }
  }, []);

  /**
   * Stop camera stream
   */
  const stopCamera = useCallback(() => {
    if (stream) {
      console.log('🛑 Stopping camera');
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    setIsOpen(false);
    setError(null);

    // Clear video element
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  }, [stream]);

  /**
   * Simple error message handler
   */
  const getSimpleErrorMessage = (error) => {
    if (error.name === 'NotAllowedError') {
      return 'Permission caméra refusée. Veuillez autoriser l\'accès à la caméra.';
    } else if (error.name === 'NotFoundError') {
      return 'Aucune caméra trouvée sur cet appareil.';
    } else if (error.name === 'NotReadableError') {
      return 'Caméra déjà utilisée par une autre application.';
    } else {
      return 'Impossible d\'accéder à la caméra. Veuillez vérifier les permissions.';
    }
  };

  /**
   * Toggle camera on/off
   */
  const toggleCamera = useCallback(async () => {
    if (isOpen) {
      stopCamera();
    } else {
      await startCamera();
    }
  }, [isOpen, startCamera, stopCamera]);

  /**
   * Simulate barcode detection (for manual input)
   */
  const simulateBarcodeDetection = useCallback((barcode) => {
    if (onBarcodeDetected && barcode) {
      onBarcodeDetected(barcode);
    }
  }, [onBarcodeDetected]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, [stream]);

  return {
    // State
    isOpen,
    error,
    stream,
    
    // Refs
    videoRef,
    
    // Methods
    startCamera,
    stopCamera,
    toggleCamera,
    simulateBarcodeDetection,
    
    // Computed
    isActive: !!(stream && stream.active),
    isSupported: !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia)
  };
};